(() => {
  'use strict';

  const theme = window.AssafTheme || {};
  const routes = theme.routes || {};
  const rootRoute = routes.root || '/';
  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const layersSelector = '.assaf-layer.is-open';
  let lastFocusedElement = null;

  const joinRoot = (path) => {
    const cleanPath = String(path || '').replace(/^\//, '');
    return `${rootRoute}${cleanPath}`.replace(/([^:]\/)\/+/, '$1');
  };

  const lockPage = () => document.body.classList.add('is-locked');
  const unlockPage = () => {
    if (!document.querySelector(layersSelector)) document.body.classList.remove('is-locked');
  };

  const setExpanded = (triggerSelector, state) => {
    qsa(triggerSelector).forEach((trigger) => trigger.setAttribute('aria-expanded', state ? 'true' : 'false'));
  };

  const openLayer = (layer, triggerSelector) => {
    if (!layer) return;
    qsa(layersSelector).forEach((open) => {
      if (open !== layer) closeLayer(open);
    });
    lastFocusedElement = document.activeElement;
    layer.hidden = false;
    requestAnimationFrame(() => {
      layer.classList.add('is-open');
      if (triggerSelector) setExpanded(triggerSelector, true);
      lockPage();
      const dialog = qs('[role="dialog"]', layer) || layer;
      const focusTarget = qs('input:not([type="hidden"]), button:not([disabled]), a[href]', dialog);
      if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 80);
    });
  };

  const closeLayer = (layer, triggerSelector) => {
    if (!layer || layer.hidden) return;
    layer.classList.remove('is-open');
    if (triggerSelector) setExpanded(triggerSelector, false);
    window.setTimeout(() => {
      layer.hidden = true;
      unlockPage();
      if (lastFocusedElement && document.contains(lastFocusedElement)) {
        lastFocusedElement.focus({ preventScroll: true });
      }
    }, 280);
  };

  const mobileMenu = () => qs('[data-mobile-menu]');
  const searchDrawer = () => qs('[data-search-drawer]');
  const languageModal = () => qs('[data-language-modal]');
  const cartDrawer = () => qs('[data-cart-drawer]');

  document.addEventListener('click', (event) => {
    const mobileOpen = event.target.closest('[data-mobile-menu-open]');
    if (mobileOpen) {
      event.preventDefault();
      openLayer(mobileMenu(), '[data-mobile-menu-open]');
      return;
    }

    const mobileClose = event.target.closest('[data-mobile-menu-close]');
    if (mobileClose) {
      event.preventDefault();
      closeLayer(mobileMenu(), '[data-mobile-menu-open]');
      return;
    }

    const searchOpen = event.target.closest('[data-search-open]');
    if (searchOpen) {
      event.preventDefault();
      openLayer(searchDrawer(), '[data-search-open]');
      return;
    }

    const searchClose = event.target.closest('[data-search-close]');
    if (searchClose) {
      event.preventDefault();
      closeLayer(searchDrawer(), '[data-search-open]');
      return;
    }

    const languageOpen = event.target.closest('[data-language-open]');
    if (languageOpen) {
      event.preventDefault();
      const menu = mobileMenu();
      const menuIsOpen = menu && menu.classList.contains('is-open');
      if (menuIsOpen) closeLayer(menu, '[data-mobile-menu-open]');
      window.setTimeout(() => openLayer(languageModal(), '[data-language-open]'), menuIsOpen ? 300 : 0);
      return;
    }

    const languageClose = event.target.closest('[data-language-close]');
    if (languageClose) {
      event.preventDefault();
      closeLayer(languageModal(), '[data-language-open]');
      return;
    }

    const cartOpen = event.target.closest('[data-cart-open]');
    if (cartOpen) {
      event.preventDefault();
      openLayer(cartDrawer(), '[data-cart-open]');
      return;
    }

    const cartClose = event.target.closest('[data-cart-close]');
    if (cartClose) {
      event.preventDefault();
      closeLayer(cartDrawer(), '[data-cart-open]');
      return;
    }

    if (!event.target.closest('[data-nav-details]')) {
      qsa('[data-nav-details][open]').forEach((details) => details.removeAttribute('open'));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    qsa(layersSelector).forEach((layer) => closeLayer(layer));
    qsa('[data-nav-details][open]').forEach((details) => details.removeAttribute('open'));
  });

  const initHeader = (scope = document) => {
    qsa('[data-header]', scope).forEach((header) => {
      if (header.dataset.headerReady === 'true') return;
      header.dataset.headerReady = 'true';
      const wrapper = header.closest('.shopify-section');
      if (header.classList.contains('assaf-header--sticky') && wrapper) wrapper.classList.add('assaf-sticky-wrapper');
      const updateScrolled = () => header.classList.toggle('is-scrolled', window.scrollY > 12);
      updateScrolled();
      window.addEventListener('scroll', updateScrolled, { passive: true });
    });
  };

  const initDesktopMenus = (scope = document) => {
    qsa('[data-nav-details]', scope).forEach((details) => {
      if (details.dataset.menuReady === 'true') return;
      details.dataset.menuReady = 'true';
      const summary = qs('summary', details);
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        qsa('[data-nav-details][open]').forEach((other) => {
          if (other !== details) other.removeAttribute('open');
        });
        if (summary) summary.setAttribute('aria-expanded', 'true');
      });
      details.addEventListener('mouseleave', () => {
        if (window.matchMedia('(hover:hover)').matches) details.removeAttribute('open');
      });
      if (summary) summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
    });
  };

  const initSliders = (scope = document) => {
    qsa('[data-product-slider]', scope).forEach((slider) => {
      if (slider.dataset.sliderReady === 'true') return;
      slider.dataset.sliderReady = 'true';
      const track = qs('[data-slider-track]', slider);
      const previous = qs('[data-slider-prev]', slider);
      const next = qs('[data-slider-next]', slider);
      if (!track) return;
      const directionMultiplier = document.documentElement.dir === 'rtl' ? -1 : 1;
      const move = (direction) => {
        const amount = Math.max(track.clientWidth * 0.82, 280);
        track.scrollBy({ left: direction * directionMultiplier * amount, behavior: 'smooth' });
      };
      if (previous) previous.addEventListener('click', () => move(-1));
      if (next) next.addEventListener('click', () => move(1));
    });
  };

  const activateGalleryMedia = (gallery, mediaId) => {
    if (!gallery || !mediaId) return;
    qsa('[data-gallery-thumb]', gallery).forEach((thumb) => thumb.classList.toggle('is-active', String(thumb.dataset.galleryThumb) === String(mediaId)));
    qsa('[data-gallery-media]', gallery).forEach((media) => media.classList.toggle('is-active', String(media.dataset.galleryMedia) === String(mediaId)));
  };

  document.addEventListener('click', (event) => {
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (thumb) {
      event.preventDefault();
      activateGalleryMedia(thumb.closest('[data-product-gallery]'), thumb.dataset.galleryThumb);
      return;
    }

    const minus = event.target.closest('[data-qty-minus]');
    if (minus) {
      const form = minus.closest('form');
      const input = form && qs('[data-qty-input]', form);
      if (input) input.value = Math.max(Number(input.min || 1), Number(input.value || 1) - 1);
      return;
    }

    const plus = event.target.closest('[data-qty-plus]');
    if (plus) {
      const form = plus.closest('form');
      const input = form && qs('[data-qty-input]', form);
      if (input) input.value = Number(input.value || 1) + 1;
    }
  });

  const formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    const value = Number(cents || 0);
    const placeholderRegex = /\{\{\s*(amount|amount_no_decimals|amount_with_comma_separator|amount_no_decimals_with_comma_separator)\s*\}\}/;
    const match = String(format).match(placeholderRegex);
    const placeholder = match ? match[1] : 'amount';
    let result;
    if (placeholder === 'amount_no_decimals') {
      result = Math.round(value / 100).toLocaleString('en-US');
    } else if (placeholder === 'amount_with_comma_separator') {
      result = (value / 100).toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
      result = Math.round(value / 100).toLocaleString('de-DE');
    } else {
      result = (value / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    return match ? String(format).replace(match[0], result) : result;
  };

  const getSelectedOptions = (form) => {
    const checked = qsa('[data-option-input]:checked', form).sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition));
    return checked.map((input) => input.value);
  };

  const updateProductVariant = (form) => {
    const variantsScript = qs('[data-product-variants]', form);
    if (!variantsScript) return;
    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      console.error('ASSAF theme: unable to parse product variants.', error);
      return;
    }

    qsa('fieldset[data-option-position]', form).forEach((fieldset) => {
      const selected = qs('[data-option-input]:checked', fieldset);
      const output = qs('[data-selected-option]', fieldset);
      if (selected && output) output.textContent = selected.value;
    });

    const selectedOptions = getSelectedOptions(form);
    const variant = variants.find((item) => item.options.length === selectedOptions.length && item.options.every((option, index) => option === selectedOptions[index]));
    const section = form.closest('[data-product-section]');
    const idInput = qs('[data-variant-id]', form);
    const addButton = qs('[data-add-to-cart]', form);
    const addText = qs('[data-add-to-cart-text]', form);
    const currentPrice = section && qs('[data-current-price]', section);
    const comparePrice = section && qs('[data-compare-price]', section);
    const sku = section && qs('[data-product-sku]', section);
    const errorOutput = qs('[data-product-form-error]', form);

    if (!variant) {
      if (idInput) idInput.value = '';
      if (addButton) addButton.disabled = true;
      if (addText) addText.textContent = theme.strings?.unavailable || 'Unavailable';
      if (errorOutput) errorOutput.hidden = true;
      return;
    }

    if (idInput) idInput.value = variant.id;
    if (addButton) addButton.disabled = !variant.available;
    if (addText) addText.textContent = variant.available ? (theme.strings?.addToCart || 'Add to cart') : (theme.strings?.soldOut || 'Sold out');
    if (currentPrice) currentPrice.innerHTML = formatMoney(variant.price);
    if (comparePrice) {
      const hasSale = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
      comparePrice.hidden = !hasSale;
      comparePrice.innerHTML = hasSale ? formatMoney(variant.compare_at_price) : '';
    }
    if (sku) sku.textContent = variant.sku || '';
    if (variant.featured_media && section) activateGalleryMedia(qs('[data-product-gallery]', section), variant.featured_media.id);
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
  };

  const initProductForms = (scope = document) => {
    qsa('[data-product-form]', scope).forEach((form) => {
      if (form.dataset.productReady === 'true') return;
      form.dataset.productReady = 'true';
      qsa('[data-option-input]', form).forEach((input) => input.addEventListener('change', () => updateProductVariant(form)));
    });
  };

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, options);
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw payload;
    return payload;
  };

  const jsRoute = (route, fallback) => {
    const value = String(route || fallback || '');
    return value.endsWith('.js') ? value : `${value}.js`;
  };

  const setButtonLoading = (button, loading) => {
    if (!button) return;
    button.classList.toggle('is-loading', loading);
    button.setAttribute('aria-busy', loading ? 'true' : 'false');
    button.disabled = loading || button.dataset.originalDisabled === 'true';
  };

  const replaceCartDrawer = (sectionHtml) => {
    if (!sectionHtml) return false;
    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const freshWrapper = qs('#shopify-section-cart-drawer', parsed);
    const currentWrapper = qs('#shopify-section-cart-drawer');
    if (freshWrapper && currentWrapper) {
      currentWrapper.replaceWith(freshWrapper);
      return true;
    }

    const freshDrawer = qs('[data-cart-drawer]', parsed);
    const currentDrawer = cartDrawer();
    if (freshDrawer && currentDrawer) {
      currentDrawer.replaceWith(freshDrawer);
      return true;
    }
    return false;
  };

  const updateCartCount = (cart) => {
    if (!cart) return;
    qsa('[data-cart-count]').forEach((count) => {
      count.textContent = cart.item_count;
      count.hidden = Number(cart.item_count) === 0;
    });
  };

  const requestCartDrawerSection = async () => {
    const sectionsUrl = `${window.location.pathname}?sections=cart-drawer`;
    const sections = await fetchJson(sectionsUrl, { headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' } });
    return sections && sections['cart-drawer'];
  };

  const refreshCart = async (openAfter = false, sectionHtml = '') => {
    let html = sectionHtml;
    if (!html) html = await requestCartDrawerSection();
    replaceCartDrawer(html);

    const cart = await fetchJson(jsRoute(routes.cart, joinRoot('cart')), {
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
    });
    updateCartCount(cart);
    if (openAfter) {
      const drawer = cartDrawer();
      if (drawer) openLayer(drawer, '[data-cart-open]');
    }
    document.dispatchEvent(new CustomEvent('assaf:cart:updated', { detail: cart }));
    return cart;
  };

  const markButtonAdded = (button) => {
    if (!button) return;
    const text = qs('[data-add-to-cart-text]', button) || qs('span', button);
    if (!text) return;
    const oldText = text.textContent;
    text.textContent = theme.strings?.addedToCart || 'Added to cart';
    button.classList.add('is-success');
    window.setTimeout(() => {
      text.textContent = oldText;
      button.classList.remove('is-success');
    }, 1300);
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-ajax-cart-form], [data-product-form]');
    if (!form) return;
    if (form.matches('[data-product-form]') && event.submitter && !event.submitter.matches('[data-add-to-cart]')) return;
    event.preventDefault();

    const submitButton = event.submitter || qs('[type="submit"]', form);
    const errorOutput = qs('[data-product-form-error]', form);
    if (submitButton) {
      submitButton.dataset.originalDisabled = submitButton.disabled ? 'true' : 'false';
      setButtonLoading(submitButton, true);
    }
    if (errorOutput) {
      errorOutput.hidden = true;
      errorOutput.textContent = '';
    }

    try {
      const formData = new FormData(form);
      formData.set('sections', 'cart-drawer');
      formData.set('sections_url', window.location.pathname);
      const result = await fetchJson(jsRoute(routes.cartAdd, joinRoot('cart/add')), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      });
      const sectionHtml = result?.sections?.['cart-drawer'] || '';
      await refreshCart(true, sectionHtml);
      markButtonAdded(submitButton);
    } catch (error) {
      console.error('ASSAF add to cart failed', error);
      const message = error.description || error.message || theme.strings?.cartError || 'Unable to add this product.';
      if (errorOutput) {
        errorOutput.textContent = message;
        errorOutput.hidden = false;
      } else {
        window.alert(message);
      }
    } finally {
      if (submitButton) {
        submitButton.dataset.originalDisabled = 'false';
        setButtonLoading(submitButton, false);
      }
    }
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-change]');
    if (!button) return;
    event.preventDefault();
    if (button.disabled) return;
    button.disabled = true;
    try {
      const response = await fetchJson(jsRoute(routes.cartChange, joinRoot('cart/change')), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          line: Number(button.dataset.cartChange),
          quantity: Math.max(0, Number(button.dataset.quantity)),
          sections: 'cart-drawer',
          sections_url: window.location.pathname
        })
      });
      const sectionHtml = response?.sections?.['cart-drawer'] || '';
      if (sectionHtml) replaceCartDrawer(sectionHtml);
      updateCartCount(response);
      if (!sectionHtml) await refreshCart(false);
      const drawer = cartDrawer();
      if (drawer) openLayer(drawer, '[data-cart-open]');
      document.dispatchEvent(new CustomEvent('assaf:cart:updated', { detail: response }));
    } catch (error) {
      console.error('ASSAF cart update failed', error);
      window.alert(error.description || error.message || theme.strings?.cartError || 'Unable to update cart.');
    } finally {
      if (document.contains(button)) button.disabled = false;
    }
  });

  const initCartCounts = () => {
    qsa('[data-cart-count]').forEach((count) => {
      count.hidden = Number(count.textContent || 0) === 0;
    });
  };

  let predictiveAbortController = null;
  let predictiveTimer = null;
  const escapeHtml = (value) => String(value || '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#039;', '"': '&quot;' }[character]));

  const renderPredictiveResults = (container, products, query) => {
    if (!container) return;
    if (!products.length) {
      container.innerHTML = `<p class="assaf-predictive-search__empty">No products found for “${escapeHtml(query)}”.</p>`;
      container.hidden = false;
      return;
    }
    container.innerHTML = products.map((product) => {
      const imageUrl = product.featured_image?.url || product.image || '';
      const image = imageUrl ? `<img src="${escapeHtml(imageUrl)}" alt="${escapeHtml(product.title)}" loading="lazy">` : '';
      return `<a class="assaf-predictive-product" href="${escapeHtml(product.url)}">${image}<span><strong>${escapeHtml(product.title)}</strong><small>${escapeHtml(product.price)}</small></span></a>`;
    }).join('') + `<a class="assaf-predictive-search__all" href="${routes.search || '/search'}?type=product&q=${encodeURIComponent(query)}">View all results</a>`;
    container.hidden = false;
  };

  const initPredictiveSearch = (scope = document) => {
    qsa('[data-predictive-search-input]', scope).forEach((input) => {
      if (input.dataset.predictiveReady === 'true') return;
      input.dataset.predictiveReady = 'true';
      const panel = input.closest('[data-search-drawer]');
      const results = panel && qs('[data-predictive-search-results]', panel);
      input.addEventListener('input', () => {
        window.clearTimeout(predictiveTimer);
        const query = input.value.trim();
        if (query.length < 2) {
          if (results) {
            results.hidden = true;
            results.innerHTML = '';
          }
          return;
        }
        predictiveTimer = window.setTimeout(async () => {
          if (predictiveAbortController) predictiveAbortController.abort();
          predictiveAbortController = new AbortController();
          try {
            const url = `${joinRoot('search/suggest.json')}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide`;
            const data = await fetchJson(url, { signal: predictiveAbortController.signal, headers: { Accept: 'application/json' } });
            const products = data?.resources?.results?.products || [];
            renderPredictiveResults(results, products, query);
          } catch (error) {
            if (error.name !== 'AbortError') console.error('ASSAF predictive search failed', error);
          }
        }, 220);
      });
    });
  };

  const initCollectionFilters = (scope = document) => {
    qsa('[data-auto-submit]', scope).forEach((input) => {
      if (input.dataset.autoSubmitReady === 'true') return;
      input.dataset.autoSubmitReady = 'true';
      input.addEventListener('change', () => {
        const form = input.closest('form');
        if (form) form.submit();
      });
    });

    qsa('.assaf-main-collection', scope).forEach((collectionSection) => {
      if (collectionSection.dataset.filtersReady === 'true') return;
      collectionSection.dataset.filtersReady = 'true';
      const panel = qs('[data-filter-panel]', collectionSection);
      const open = qs('[data-filter-open]', collectionSection);
      const close = qs('[data-filter-close]', collectionSection);
      if (open && panel) open.addEventListener('click', () => panel.classList.add('is-open'));
      if (close && panel) close.addEventListener('click', () => panel.classList.remove('is-open'));
    });
  };

  const initScrollTop = () => {
    const button = qs('[data-scroll-top]');
    if (!button || button.dataset.ready === 'true') return;
    button.dataset.ready = 'true';
    const update = () => button.classList.toggle('is-visible', window.scrollY > 500);
    window.addEventListener('scroll', update, { passive: true });
    update();
    button.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
  };

  const init = (scope = document) => {
    initHeader(scope);
    initDesktopMenus(scope);
    initSliders(scope);
    initProductForms(scope);
    initPredictiveSearch(scope);
    initCollectionFilters(scope);
    initCartCounts();
    initScrollTop();
  };

  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
